import { z } from 'zod'

import { LaneRationaleCitationSchema, LaneRationaleSchema } from './agent/evidence.ts'
import { idSchema, msTimestampSchema, PrivacyLevelEnum } from './common.ts'

// RAL-219: one row per (proposal, channel). The proposal is the creative decision; the delivery is
// that decision landing on one channel, so publish state lives here rather than on the proposal.
export const DeliveryStatusEnum = z.enum(['scheduled', 'publishing', 'published', 'failed', 'cancelled', 'slot_missed'])
export type DeliveryStatus = z.infer<typeof DeliveryStatusEnum>

// Why a lane was cancelled: a campaign stopped under it (RAL-226), or the creator cancelled it from the
// Queue (RAL-237). Null for every other cancel.
export const DeliveryCancelReasonEnum = z.enum(['campaign_replaced', 'campaign_ended', 'user_cancelled'])
export type DeliveryCancelReason = z.infer<typeof DeliveryCancelReasonEnum>

export const DeliveryFailureCodeEnum = z.enum(['publish_settings_missing', 'privacy_level_option_mismatch'])
export type DeliveryFailureCode = z.infer<typeof DeliveryFailureCodeEnum>

export const DeliverySchema = z.object({
  proposalId: idSchema,
  channelId: idSchema,
  campaignId: idSchema,
  workspaceId: idSchema,
  assetId: idSchema,
  caption: z.string().min(1),
  hashtags: z.array(z.string()).default([]),
  title: z.string().nullable().default(null),
  // Copied from the approved lane (RAL-239) so the Queue can still say why once the lane is scheduled.
  rationale: LaneRationaleSchema.nullable().default(null),
  rationaleCitation: LaneRationaleCitationSchema.nullable().default(null),
  scheduledAt: msTimestampSchema,
  status: DeliveryStatusEnum.default('scheduled'),
  videoId: idSchema.nullable().default(null),
  // TikTok's ids for a published lane, kept on the delivery so a live post is recorded even when its
  // Video row could not be written.
  publishId: z.string().nullable().default(null),
  tiktokVideoId: z.string().nullable().default(null),
  error: z.string().nullable().default(null),
  cancelReason: DeliveryCancelReasonEnum.nullable().default(null),
  privacyLevel: PrivacyLevelEnum.nullable().default(null),
  musicUsageConfirmedAt: msTimestampSchema.nullable().default(null),
  failureCode: DeliveryFailureCodeEnum.nullable().default(null),
  createdAt: msTimestampSchema,
  updatedAt: msTimestampSchema
})
export type DeliveryRow = z.infer<typeof DeliverySchema>

// RAL-224: one `publish-delivery` event per scheduled delivery. `scheduledAt` identifies which
// schedule the event was published for, so an event outlived by a reschedule or retry is a no-op.
export const PublishDeliveryEventBodySchema = z.object({
  proposalId: idSchema,
  channelId: idSchema,
  scheduledAt: msTimestampSchema
})
export type PublishDeliveryEventBody = z.infer<typeof PublishDeliveryEventBodySchema>

// Moves one approved lane to a new time; the other lanes of the proposal keep theirs.
export const DeliveryRescheduleBodySchema = z.object({ scheduledAt: msTimestampSchema }).strict()
export type DeliveryRescheduleBody = z.infer<typeof DeliveryRescheduleBodySchema>

// One published lane of an asset, newest first: the agent reads these as the clip's history, and the
// Queue shows the latest one on the proposal it was read for (RAL-228).
export const DeliveryHistoryEntrySchema = z.object({
  channelId: idSchema,
  handle: z.string(),
  postedAt: msTimestampSchema,
  views: z.number().int().nonnegative().nullable(),
  // The campaign that posted it, so the asset detail can say which push a post belonged to.
  campaignId: idSchema.nullable().default(null)
})
export type DeliveryHistoryEntry = z.infer<typeof DeliveryHistoryEntrySchema>

export const DeliveryRetryBodySchema = z.union([
  z.object({}).strict(),
  z
    .object({
      privacyLevel: PrivacyLevelEnum,
      musicUsageConfirmed: z.literal(true)
    })
    .strict()
])
export type DeliveryRetryBody = z.infer<typeof DeliveryRetryBodySchema>

export const DeliveryResponseSchema = z.object({ delivery: DeliverySchema })
export type DeliveryResponse = z.infer<typeof DeliveryResponseSchema>

// The card-level summary of a proposal's lanes. Computed from the deliveries on every read and
// never stored, so it can't drift from the rows it summarizes.
export const ProposalDeliveryStateEnum = z.enum([
  'scheduled',
  'published',
  'partial',
  'failed',
  'slot_missed',
  'cancelled'
])
export type ProposalDeliveryState = z.infer<typeof ProposalDeliveryStateEnum>

const IN_FLIGHT = new Set<DeliveryStatus>([DeliveryStatusEnum.enum.scheduled, DeliveryStatusEnum.enum.publishing])

// Precedence: all published, then any published (partial), then anything still in flight, then any
// failure, then any missed slot (it beats cancelled because a creator can still reschedule it). What's
// left is only cancelled lanes. Null when there are no deliveries (the proposal was never approved).
export function deriveProposalDeliveryState(
  deliveries: ReadonlyArray<Pick<DeliveryRow, 'status'>>
): ProposalDeliveryState | null {
  if (deliveries.length === 0) return null
  let published = 0
  let inFlight = false
  let failed = false
  let missed = false
  for (const { status } of deliveries) {
    if (status === DeliveryStatusEnum.enum.published) published++
    else if (IN_FLIGHT.has(status)) inFlight = true
    else if (status === DeliveryStatusEnum.enum.failed) failed = true
    else if (status === DeliveryStatusEnum.enum.slot_missed) missed = true
  }
  if (published === deliveries.length) return ProposalDeliveryStateEnum.enum.published
  if (published > 0) return ProposalDeliveryStateEnum.enum.partial
  if (inFlight) return ProposalDeliveryStateEnum.enum.scheduled
  if (failed) return ProposalDeliveryStateEnum.enum.failed
  if (missed) return ProposalDeliveryStateEnum.enum.slot_missed
  return ProposalDeliveryStateEnum.enum.cancelled
}
